import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';

const DURATION = 60;

function makeEq() {
  const op = ['+', '-', '×'][Math.floor(Math.random() * 3)];
  let a, b, ans;
  if (op === '×') { a = 2 + Math.floor(Math.random() * 11); b = 2 + Math.floor(Math.random() * 11); ans = a * b; }
  else if (op === '+') { a = 2 + Math.floor(Math.random() * 19); b = 2 + Math.floor(Math.random() * 19); ans = a + b; }
  else { a = 12 + Math.floor(Math.random() * 28); b = 2 + Math.floor(Math.random() * 10); ans = a - b; }
  const wrongs = new Set();
  while (wrongs.size < 3) { const w = ans + (Math.random() < 0.5 ? 1 : -1) * (1 + Math.floor(Math.random() * 9)); if (w !== ans && w > 0) wrongs.add(w); }
  return { text: `${a} ${op} ${b}`, ans, opts: [ans, ...wrongs].sort(() => Math.random() - 0.5) };
}

function FlashEquationGame({ lang, onBack }) {
  const nl = lang === 'nl';
  const [phase, setPhase] = useState('idle'); // idle|flash|answer|feedback|done
  const [eq, setEq] = useState(null);
  const [score, setScore] = useState(0);
  const [count, setCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(DURATION);
  const [fb, setFb] = useState(null);
  const [best, setBest] = useStorage('gm_flashequation_best', 0);
  const timerRef = useRef(null); const flashRef = useRef(null); const scoreRef = useRef(0);

  useEffect(() => () => { clearInterval(timerRef.current); clearTimeout(flashRef.current); }, []);

  const endGame = (s) => { clearInterval(timerRef.current); clearTimeout(flashRef.current); saveGameScore('flashequation', s); if (s > best) setBest(s); setPhase('done'); };

  const nextQ = (s, c) => {
    if (c >= 20) { endGame(s); return; }
    setEq(makeEq()); setPhase('flash');
    flashRef.current = setTimeout(() => setPhase('answer'), 1500);
  };

  const start = () => {
    scoreRef.current = 0; setScore(0); setCount(0); setTimeLeft(DURATION); setFb(null);
    setEq(makeEq()); setPhase('flash');
    flashRef.current = setTimeout(() => setPhase('answer'), 1500);
    timerRef.current = setInterval(() => setTimeLeft(t => { if (t <= 1) { endGame(scoreRef.current); return 0; } return t - 1; }), 1000);
  };

  const handleAnswer = (val) => {
    clearTimeout(flashRef.current);
    const ok = val === eq.ans;
    if (ok) scoreRef.current++;
    const ns = scoreRef.current, nc = count + 1;
    setScore(ns); setCount(nc); setFb(ok ? 'correct' : 'wrong'); setPhase('feedback');
    flashRef.current = setTimeout(() => { setFb(null); nextQ(ns, nc); }, 500);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
      </div>

      {phase === 'idle' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🌀</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Flitssom' : 'Flash Equation'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.7 }}>
            {nl ? 'Een som flitst 1.5s. Kies het juiste antwoord. 20 sommen, 60 seconden.' : 'An equation flashes for 1.5s. Pick the right answer. 20 equations, 60 seconds.'}
          </div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {best}</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(phase === 'flash' || phase === 'answer' || phase === 'feedback') && eq && (
        <div>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 10 }), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: C.white, fontWeight: 700 }}>✓ {score}</div>
            <div style={{ fontSize: 13, color: timeLeft < 10 ? C.red : C.silver }}>{timeLeft}s</div>
            <div style={{ fontSize: 12, color: C.silver }}>{count}/20</div>
          </div>
          <div style={{ height: 6, background: _T.bg3, borderRadius: 4, marginBottom: 16, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(timeLeft / DURATION) * 100}%`, background: `linear-gradient(90deg,${_T.grad1},${_T.grad2})`, borderRadius: 4, transition: 'width 1s linear' }} />
          </div>
          <div style={{ ...s_GC({ textAlign: 'center', padding: 32, marginBottom: 12 }) }}>
            {phase === 'flash' && <div style={{ fontSize: 30, color: C.white, fontWeight: 700, letterSpacing: 2 }}>{eq.text} = ?</div>}
            {phase === 'feedback' && <div style={{ fontSize: 40, color: fb === 'correct' ? '#4AC87A' : C.red }}>{fb === 'correct' ? '✓' : '✗'}</div>}
            {phase === 'answer' && <div style={{ fontSize: 22, color: C.silver }}>?</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {eq.opts.map((opt, i) => (
              <button key={i} onClick={() => phase === 'answer' && handleAnswer(opt)} style={s_PB({ fontSize: 20, padding: '16px 8px', opacity: phase !== 'answer' ? 0.4 : 1, cursor: phase === 'answer' ? 'pointer' : 'default' })}>{opt}</button>
            ))}
          </div>
        </div>
      )}

      {phase === 'done' && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🌀</div>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700 }}>{score} / 20</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'correct' : 'correct'}</div>
          {best > 0 && <div style={{ color: C.teal, fontSize: 13, marginBottom: 12 }}>🏆 Best: {best}</div>}
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default FlashEquationGame;
